/**
 * FissalPrime.jsx — owner-only control panel for the privileged Fissal tier.
 *
 * Read path : /api/v2/widgets/fissal-prime (owner)
 * Write path: /api/prime/routing, /api/prime/proposals/:id/:decision, /api/prime/sweep
 *
 * Lets Echo pick Prime's base model, the cheap scout/surgical models used for
 * delegation, and a per-agent model override for each .github/agents persona.
 */
import { Show, For, createSignal, createMemo } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { maskModelName } from '../lib/format.js';
import './FissalPrime.css';

function fmtAgo(ts) {
  if (!ts) return 'never';
  const ago = Math.round((Date.now() - ts) / 1000);
  if (ago < 60) return ago + 's ago';
  if (ago < 3600) return Math.round(ago / 60) + 'm ago';
  if (ago < 86400) return Math.round(ago / 3600) + 'h ago';
  return Math.round(ago / 86400) + 'd ago';
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

function ModelPicker(props) {
  return (
    <label class="fp-field">
      <span class="fp-field-label">{props.label}</span>
      <input
        class="fp-input"
        list="fp-model-list"
        value={props.value || ''}
        placeholder={props.placeholder}
        onChange={e => props.onChange(e.currentTarget.value.trim())}
      />
      <Show when={props.hint}><span class="fp-hint">{props.hint}</span></Show>
    </label>
  );
}

export function FissalPrimePanel(props = {}) {
  const [nonce, setNonce] = createSignal(0);
  const { data, loading, error } = useWidgetData('fissal-prime', () => ({ n: nonce() }));
  const [busy, setBusy] = createSignal('');
  const [notice, setNotice] = createSignal('');

  const routing = createMemo(() => data()?.routing || {});
  const agents = createMemo(() => data()?.agents || []);
  const models = createMemo(() => data()?.availableModels || []);

  const reload = () => setNonce(n => n + 1);

  async function saveRouting(patch) {
    setBusy('routing');
    try {
      await postJson('/api/prime/routing', patch);
      setNotice('Routing saved.');
      reload();
    } catch (e) {
      setNotice(`Save failed (${e.message}).`);
    } finally {
      setBusy('');
    }
  }

  async function decide(id, decision) {
    setBusy(`proposal-${id}`);
    try {
      await postJson(`/api/prime/proposals/${id}/${decision}`, {});
      reload();
    } catch (e) {
      setNotice(`Could not ${decision} proposal #${id} (${e.message}).`);
    } finally {
      setBusy('');
    }
  }

  async function runSweep() {
    setBusy('sweep');
    setNotice('Sweeping…');
    try {
      const result = await postJson('/api/prime/sweep', { remediate: true });
      setNotice(result.quiet ? 'Sweep clean — nothing of note.' : 'Sweep found items; a new chat session is waiting.');
      reload();
    } catch (e) {
      setNotice(`Sweep failed (${e.message}).`);
    } finally {
      setBusy('');
    }
  }

  return (
    <div class={`fp-root ${props.class || ''}`}>
      <datalist id="fp-model-list">
        <For each={models()}>{m => <option value={m}>{maskModelName(m)}</option>}</For>
      </datalist>

      <Show when={error()}><div class="fp-error">Could not load Prime status.</div></Show>
      <Show when={loading() && !data()}><div class="fp-loading">Loading…</div></Show>

      <Show when={data()}>
        <header class="fp-hero">
          <div class="fp-hero-main">
            <span class={`fp-dot ${data().armed ? 'is-armed' : 'is-dormant'}`} />
            <strong>{data().armed ? 'Prime armed' : 'Prime dormant'}</strong>
          </div>
          <Show when={!data().armed}>
            <p class="fp-hint">Set <code>FISSAL_PRIME_SECRET</code> (32+ chars) and restart to enable.</p>
          </Show>
          <div class="fp-hero-meta">
            Last sweep: {fmtAgo(data().lastSweep?.at)}
            <button class="fp-btn" disabled={busy() === 'sweep'} onClick={runSweep}>
              {busy() === 'sweep' ? 'Sweeping…' : 'Sweep now'}
            </button>
          </div>
        </header>

        <Show when={notice()}><div class="fp-notice">{notice()}</div></Show>

        <section class="fp-section">
          <h4>Models</h4>
          <ModelPicker
            label="Base model" value={routing().primeModel}
            hint="Must support OpenAI-style tool calling (openrouter:, opencode:, opencode-go:, cheaperinference:, openai:)."
            onChange={v => saveRouting({ primeModel: v })}
          />
          <ModelPicker
            label="Scout (cheap/free)" value={routing().scoutModel}
            hint="Bulk reading and summarising. Prime verifies its claims before trusting them."
            onChange={v => saveRouting({ scoutModel: v })}
          />
          <ModelPicker
            label="Surgical (fast)" value={routing().surgicalModel}
            hint="Small structured edits where correctness is cheap to check."
            onChange={v => saveRouting({ surgicalModel: v })}
          />
        </section>

        <section class="fp-section">
          <h4>Failover chains</h4>
          <p class="fp-hint">
            If the chosen model is out of credit, rate-limited or withdrawn, Prime walks its
            chain across both providers rather than failing the request.
          </p>
          <For each={['prime', 'scout', 'surgical']}>{tier => (
            <div class="fp-chain">
              <span class="fp-chain-tier">{tier}</span>
              <div class="fp-chain-models">
                <For each={data().chains?.[tier] || []}>{model => {
                  const cool = () => (data().cooldowns || []).find(c => c.model === model);
                  return (
                    <span
                      class={`fp-chip ${cool() ? 'is-cooling' : ''}`}
                      title={cool() ? `${cool().reason} — retrying in ${cool().seconds_remaining}s` : maskModelName(model)}
                    >{maskModelName(model).replace(/^(openrouter|opencode-go|opencode|cheaperinference|openai):/, '')}</span>
                  );
                }}</For>
              </div>
            </div>
          )}</For>
          <Show when={(data().cooldowns || []).length}>
            <p class="fp-hint fp-hint-warn">
              {data().cooldowns.length} model(s) sidelined right now — hover a dimmed chip for the reason.
            </p>
          </Show>
        </section>

        <section class="fp-section">
          <h4>Agent routing <span class="fp-count">{agents().length}</span></h4>
          <p class="fp-hint">Blank uses the scout model. Set a model to pin that specialist.</p>
          <div class="fp-agents">
            <For each={agents()}>{agent => (
              <div class="fp-agent">
                <div class="fp-agent-name" title={agent.description}>{agent.name}</div>
                <input
                  class="fp-input fp-input-sm"
                  list="fp-model-list"
                  value={agent.model || ''}
                  placeholder={`inherit → ${routing().scoutModel || 'scout'}`}
                  onChange={e => saveRouting({ agents: { [agent.slug]: e.currentTarget.value.trim() } })}
                />
              </div>
            )}</For>
          </div>
        </section>

        <section class="fp-section">
          <h4>Proposals <span class="fp-count">{data().proposals.length}</span></h4>
          <Show when={!data().proposals.length}><p class="fp-empty">Nothing awaiting your decision.</p></Show>
          <For each={data().proposals}>{p => (
            <div class="fp-proposal">
              <div class="fp-proposal-title">{p.title}</div>
              <div class="fp-proposal-why">{p.rationale}</div>
              <Show when={p.command}><code class="fp-cmd">{p.command}</code></Show>
              <div class="fp-proposal-actions">
                <span class="fp-hint">{fmtAgo(p.ts)}</span>
                <button class="fp-btn" disabled={busy() === `proposal-${p.id}`} onClick={() => decide(p.id, 'approve')}>Approve</button>
                <button class="fp-btn fp-btn-ghost" disabled={busy() === `proposal-${p.id}`} onClick={() => decide(p.id, 'reject')}>Reject</button>
              </div>
            </div>
          )}</For>
        </section>

        <section class="fp-section">
          <h4>Recent activity</h4>
          <Show when={!data().journal.length}><p class="fp-empty">No activity yet.</p></Show>
          <ul class="fp-journal">
            <For each={data().journal}>{entry => (
              <li class={entry.ok ? '' : 'is-fail'}>
                <span class="fp-journal-kind">{entry.kind}</span>
                <span class="fp-journal-detail">{entry.detail}</span>
                <span class="fp-journal-ago">{fmtAgo(entry.ts)}</span>
              </li>
            )}</For>
          </ul>
        </section>
      </Show>
    </div>
  );
}

export default function FissalPrime() {
  return <FissalPrimePanel class="v2-widget" />;
}
